import { Link, unstable_usePrompt, useParams } from 'react-router-dom';
import {
    setEventTypesQuery,
    useBusinessInfo,
    useEventTypeDetailsQuery,
    useEventTypesQuery,
} from '../../../lib/Queries';
import { useEffect, useRef, useState } from 'react';
import Loading from '../../../components/Loading';
import useBusinessPostApi from '../../../lib/useBusinessPostApi.js';
import useBusinessNavigate, {
    getBusinessUrl,
} from '../../../lib/useBusinessNavigate.js';
import * as Toast from '../../../lib/Toast.js';
import TextInput from '../../../components/TextInput';
import CheckboxList from '../../../components/CheckboxList.jsx';
import DurationInput from '../../../components/DurationInput.jsx';
import BookableResourcesCheckboxes from '../../../components/BookableResourcesCheckboxes.jsx';
import MoneyInput from '../../../components/MoneyInput.jsx';
import Toggle from '../../../components/Toggle.jsx';
import ErrorMessage from '../../../components/ErrorMessage';
import ClassInstances from '../../../components/scheduling/ClassInstances.jsx';
import Select from '../../../components/Select';
import DropdownButton from '../../../components/DropdownButton.jsx';
import { XCircleIcon } from '@heroicons/react/24/solid';
import GoogleSocialIcon from '../../../components/icons/GoogleSocialIcon.jsx';
import { LinkIcon, MapPinIcon } from '@heroicons/react/24/outline/index.js';
import SelectWithIcon from '../../../components/SelectWithIcon.jsx';
import Breadcrumb from '../../../components/Breadcrumb.jsx';
import PageHeading from '../../../components/PageHeading';
import DotDotDotMenu from '../../../components/DotDotDotMenu';
import CopyIcon from '../../../components/icons/CopyIcon.jsx';
import useDelete from '../../../lib/useDelete.jsx';
import {
    useDoDeleteEventType,
    useDoDuplicateEventType,
} from '../../../lib/useEventTypeActions.js';
import { useCompleteStep } from '../../../lib/Onboarding.js';
import TextArea from '../../../components/TextArea.jsx';
import FileUpload from '../../../components/FileUpload';
import ZoomIcon from '../../../components/icons/ZoomIcon.jsx';
import WarnIfZoomNotConfigured from '../../../components/integrations/WarnIfZoomNotConfigured.jsx';
import WarnIfGoogleNotConfigured from '../../../components/integrations/WarnIfGoogleNotConfigured.jsx';
import InfoMessageBlock from '../../../components/InfoMessageBlock.jsx';
import GateRequiresPaymentProcessor from '~/components/GateRequiresPaymentProcessor';
import NumberBookableResourcesRequiredSetting from '~/components/scheduling/NumberBookableResourcesRequiredSetting.jsx';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import ErrorMessageBlock from '~/components/ErrorMessageBlock.jsx';
import useBlockUnsavedChanges from '~/lib/useBlockUnsavedChanges.js';
import { MINUTES_IN_A_DAY } from '~/lib/DateTime.js';

const typeOfTypeToDefaultName = {
    event: 'New Event Type',
    series: 'New Class Series',
    class: 'New Class Type',
};

const EventTypeEditPage = () => {
    const { typeId } = useParams();
    const url = new URL(document.location.href);
    const urlType = url.searchParams.get('type');

    const { data: businessData } = useBusinessInfo();
    const uniqueKeyRef = useRef(new Date().getTime());
    const hasPackages = !!businessData?.hasPackages;
    const {
        isLoading,
        data,
        refetch: refetchDetails,
    } = useEventTypeDetailsQuery(typeId, urlType ? `type=${urlType}` : '', uniqueKeyRef.current);
    const { refetch } = useEventTypesQuery();
    const [typeData, setTypeData] = useState();
    const [groupLabels, setGroupLabels] = useState({});
    const [hasSaved, setHasSaved] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const navigate = useBusinessNavigate();
    const { save, isSaving } = useBusinessPostApi(`/event-types/${typeId}`);
    const { complete } = useCompleteStep();

    const [isAddingPadding, setIsAddingPadding] = useState(false);

    const showPadding =
        isAddingPadding ||
        typeData?.padding_end > 0 ||
        typeData?.padding_start > 0;

    const canUsePadding = showPadding || (typeData?.duration > 0 && typeData?.duration%MINUTES_IN_A_DAY !== 0);

    const typeDataFromServer = {
        ...data?.type,
        bookable_resources: data?.bookable_resources,
    };

    const isDirty =
        !hasSaved &&
        (Object.keys(groupLabels).length > 0 ||
            JSON.stringify(typeDataFromServer) !== JSON.stringify(typeData));

    const isClass = typeData?.is_class;
    const isSeries = typeData?.type === 'series';
    const isNew = typeId <= 0;

    const isDayRange = typeData?.duration > 0 && typeData?.duration%MINUTES_IN_A_DAY === 0 && !!typeData?.settings?.max_days;

    const defaultPageHeading =
        typeOfTypeToDefaultName[typeData?.type] || 'New Event Type';

    const allCategories = data?.all_categories || [];
    const categoryOptions = { 0: 'None' };
    for (const c of allCategories) {
        categoryOptions[c.id] = c.name;
    }

    useBlockUnsavedChanges(isDirty);

    useEffect(() => {
        if (typeof data?.type === 'undefined') {
            return;
        }

        // Make sure we're up to date after save
        //  BUT if a user starts editing, we shouldn't clobber
        if (!typeData || typeof typeData?.bookable_resources === 'undefined') {
            const newData = data?.type || {};
            newData.bookable_resources = data?.bookable_resources;
            setTypeData(newData);
            setIsLoaded(true);
        } else if (data?.type?.classes) {
            // Still update class offerings...
            setTypeData(old => ({ ...old, classes: data?.type?.classes }));
        }
    }, [JSON.stringify(data)]);

    function submit(ev) {
        ev.preventDefault();
        setHasSaved(true);

        save({
            ...typeData,
            groupLabels: groupLabels,
        }).then(res => {
            refetch();
            refetchDetails();

            // Server tells us if this was the first real event type (not the default)
            const navigatePath = res?.showAvailabilityNudge
                ? `/scheduling/event-types?showAvailabilityNudge=true`
                : `/scheduling/event-types`;

            navigate(navigatePath);

            if (res?.classForm) {
                Toast.success(
                    `Successfully saved event type and added a new form, "${res.classForm.name}", that uses the class booking layout.`,
                );
            } else {
                Toast.success('Successfully saved event type');
            }

            complete('scheduling-event-types');
        });
    }

    function setPayment(value) {
        setTypeData(old => ({
            ...old,
            settings: { ...old.settings, payment: value },
        }));
    }

    const { doDelete, isDeleting } = useDoDeleteEventType(typeData?.id);
    const { doDuplicate, isDuplicating } = useDoDuplicateEventType(
        typeData?.id,
    );

    const isPayInFull =
        !typeData?.settings?.payment || typeData?.settings?.payment === 'full';

    return (
        <div className="">
            <PageHeading
                heading={
                    isLoading
                        ? 'Loading...'
                        : typeData?.name || defaultPageHeading
                }
                crumbs={[
                    {
                        name: 'Event Types',
                        href: '/scheduling/event-types',
                    },
                ]}
                rightContent={
                    !isNew && (
                        <DotDotDotMenu
                            btnClassName="btn-gray-circle"
                            options={[
                                {
                                    name: 'Duplicate',
                                    icon: <CopyIcon />,
                                    onClick: () => doDuplicate(),
                                },
                                {
                                    name: isDeleting ? 'Deleting...' : 'Delete',
                                    onClick: () => doDelete(),
                                    isDelete: true,
                                },
                            ]}
                        />
                    )
                }
            />
            {isLoading && <Loading />}
            {!isLoading && (
                <form onSubmit={submit} className="mt-6">
                    {typeData?.is_class &&
                        !typeData?.classes?.length &&
                        !isNew && (
                            <ErrorMessageBlock className="mb-4 max-w-2xl">
                                This class is not currently being offered at any
                                times. Please add times that this class is
                                offered at by clicking{' '}
                                <span className="font-semibold">
                                    {isSeries ? 'Add New Session' : 'Add Class'}
                                </span>{' '}
                                below.
                            </ErrorMessageBlock>
                        )}

                    <div className="panel-label">Event Details</div>
                    <div className="panel p-6">
                        <TextInput
                            id="name"
                            label="Name"
                            inputClassName="w-full"
                            autoFocus={!typeData?.name}
                            value={typeData?.name || ''}
                            onChange={newValue =>
                                setTypeData({ ...typeData, name: newValue })
                            }
                        />

                        {allCategories.length > 0 && isLoaded && (
                            <Select
                                value={`${typeData?.event_type_category_id}`}
                                onChange={value => {
                                    setTypeData(old => ({
                                        ...old,
                                        event_type_category_id: value,
                                    }));
                                }}
                                label="Category"
                                id="event_type_category_id"
                                className="mt-6"
                                options={categoryOptions}
                            />
                        )}
                        <DurationInput
                            id="duration"
                            label="Duration"
                            className="mt-6 w-48"
                            value={typeData?.duration || ''}
                            onChange={newValue =>
                                setTypeData(old => ({
                                    ...old,
                                    duration: newValue,
                                }))
                            }
                            allowVariableMultiday={!isClass}
                            minDays={
                                typeData?.duration > 0
                                    ? parseInt(
                                          typeData?.duration / MINUTES_IN_A_DAY,
                                      )
                                    : ''
                            }
                            maxDays={typeData?.settings?.max_days}
                            onMinDaysChange={newValue =>
                                setTypeData(old => ({
                                    ...old,
                                    duration: newValue
                                        ? parseInt(newValue * MINUTES_IN_A_DAY)
                                        : -1,
                                }))
                            }
                            onMaxDaysChange={newValue => {
                                console.log('Setting maxDays = ', newValue);
                                setTypeData(old => ({
                                    ...old,
                                    settings: {
                                        ...(old?.settings || {}),
                                        max_days: newValue,
                                    },
                                }));
                            }}
                        />
                        {!showPadding && canUsePadding && (
                            <button
                                type="button"
                                className="mt-1 text-sm underline hover:text-gray-600 text-gray-500"
                                onClick={ev => setIsAddingPadding(true)}
                            >
                                Block off extra time before or after
                            </button>
                        )}
                        {showPadding && (
                            <div className="flex">
                                <DurationInput
                                    id="padding_start"
                                    label="Padding Before Start"
                                    className="mt-6 mr-6 w-48"
                                    value={typeData?.padding_start}
                                    onChange={newValue =>
                                        setTypeData({
                                            ...typeData,
                                            padding_start: newValue,
                                        })
                                    }
                                    excludeOptions={['days']}
                                />
                                <DurationInput
                                    id="padding_end"
                                    label="Padding After End"
                                    className="mt-6 w-48"
                                    value={typeData?.padding_end}
                                    onChange={newValue =>
                                        setTypeData({
                                            ...typeData,
                                            padding_end: newValue,
                                        })
                                    }
                                    excludeOptions={['days']}
                                />
                            </div>
                        )}
                        <TextArea
                            value={typeData?.description || ''}
                            onChange={value =>
                                setTypeData({ ...typeData, description: value })
                            }
                            label="Description"
                            id="description"
                            className="mt-6"
                        />
                        <div className="input-label mt-6">Image</div>
                        <FileUpload
                            value={typeData?.image}
                            onChange={value =>
                                setTypeData({ ...typeData, image: value })
                            }
                            id="image"
                            accept="image/*"
                        />
                        {isClass && (
                            <TextInput
                                id="settings.max_attendees"
                                label="Max Attendees"
                                type="number"
                                className="mt-4 max-w-48"
                                required={true}
                                value={typeData?.settings?.max_attendees || ''}
                                onChange={newValue =>
                                    setTypeData({
                                        ...typeData,
                                        settings: {
                                            ...typeData?.settings,
                                            max_attendees: Math.max(
                                                newValue,
                                                0,
                                            ),
                                        },
                                    })
                                }
                            />
                        )}
                        {/*Toggle to enable late joining for series*/}
                        {isSeries && (
                            <div className="flex items-start mt-4">
                                <Toggle
                                    enabled={!!typeData?.settings?.late_join}
                                    setEnabled={value =>
                                        setTypeData({
                                            ...typeData,
                                            settings: {
                                                ...typeData?.settings,
                                                late_join: value,
                                            },
                                        })
                                    }
                                />
                                <div className="ml-2">
                                    Allow clients to enroll in a series already in progress
                                    {!!typeData?.settings?.late_join && (
                                        <div className="text-gray-500 text-sm">
                                            The price will be prorated based on the number of sessions remaining in the series.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="panel-label mt-6">Location</div>
                    <div className="panel p-6">
                        <SelectWithIcon
                            value={typeData?.location?.type}
                            onChange={newValue =>
                                setTypeData(old => ({
                                    ...old,
                                    location: {
                                        ...old?.location,
                                        type: newValue,
                                    },
                                }))
                            }
                            btnClass={`${typeData?.location?.type === 'custom' || typeData?.location?.type === 'address' ? 'rounded-b-none' : ''}`}
                            options={[
                                {
                                    name: 'None',
                                    icon: <XCircleIcon />,
                                    value: 'none',
                                },
                                {
                                    name: 'Google Meet',
                                    icon: <GoogleSocialIcon />,
                                    value: 'google',
                                },
                                {
                                    name: 'Zoom',
                                    icon: <ZoomIcon size={20} />,
                                    value: 'zoom',
                                },
                                {
                                    name: 'In Person',
                                    icon: <MapPinIcon />,
                                    value: 'address',
                                },
                                {
                                    name: 'From Form Question',
                                    icon: <DocumentDuplicateIcon />,
                                    value: 'form',
                                },
                                {
                                    name: 'Custom Meeting Link',
                                    icon: (
                                        <LinkIcon className="text-primary-700" />
                                    ),
                                    value: 'custom',
                                },
                            ]}
                        />
                        {typeData?.location?.type === 'form' && (
                            <InfoMessageBlock className="mt-4">
                                <div>
                                    Please make sure your form has a Location
                                    question. The location for events will be
                                    set to what clients enter for the Location
                                    question.
                                </div>
                            </InfoMessageBlock>
                        )}
                        {typeData?.location?.type === 'custom' && (
                            <TextInput
                                id="location"
                                value={typeData?.location?.location || ''}
                                onChange={newValue =>
                                    setTypeData({
                                        ...typeData,
                                        location: {
                                            ...typeData?.location,
                                            location: newValue,
                                        },
                                    })
                                }
                                inputClassName="!max-w-full !rounded-t-none border-t-0 focus:!border-gray-300"
                                placeholder="https://my-custom-meeting-link"
                                autoFocus={!typeData?.location?.location}
                            />
                        )}
                        {typeData?.location?.type === 'address' && (
                            <TextArea
                                id="location"
                                value={typeData?.location?.location || ''}
                                onChange={newValue =>
                                    setTypeData({
                                        ...typeData,
                                        location: {
                                            ...typeData?.location,
                                            location: newValue,
                                        },
                                    })
                                }
                                inputClassName="!max-w-full !rounded-t-none border-t-0 focus:!border-gray-300"
                                placeholder="Address or meeting location information"
                                autoFocus={!typeData?.location?.location}
                            />
                        )}
                        {typeData?.location?.type === 'zoom' && (
                            <WarnIfZoomNotConfigured
                                className="mt-4"
                                message={
                                    <div>
                                        Zoom is not configured. Please set up
                                        Zoom in{' '}
                                        <Link
                                            className="text-white"
                                            to={getBusinessUrl(
                                                '/integrations/other',
                                            )}
                                        >
                                            Integrations &gt; Other
                                        </Link>{' '}
                                        to set up meeting links.
                                    </div>
                                }
                            />
                        )}
                        {typeData?.location?.type === 'google' && (
                            <WarnIfGoogleNotConfigured
                                className="mt-4"
                                message={
                                    <div>
                                        Google is not configured. Please connect
                                        to a Google calendar in{' '}
                                        <Link
                                            className="text-white"
                                            to={getBusinessUrl('/integrations')}
                                        >
                                            Integrations
                                        </Link>{' '}
                                        to set up meeting links.
                                    </div>
                                }
                            />
                        )}
                    </div>

                    <div className="panel-label mt-6">Price &amp; Payment</div>
                    <div className="panel p-6">
                        <div className="flex items-end gap-x-2">
                            <MoneyInput
                                id="price"
                                label="Price"
                                placeholder="Free"
                                value={typeData?.price || ''}
                                onChange={newValue =>
                                    setTypeData({ ...typeData, price: newValue })
                                }
                                className="max-w-32"
                            />
                            {isDayRange && (
                                <Select
                                    id="pricing_per"
                                    className="mt-4 w-48"
                                    value={typeData?.settings?.pricing_per || 'day'}
                                    onChange={newValue =>
                                        setTypeData(old => ({
                                            ...old,
                                            settings: {
                                                ...(old?.settings || {}),
                                                pricing_per: newValue,
                                            },
                                        }))
                                    }
                                    options={[
                                        { value: 'day', label: 'Per day' },
                                        { value: 'night', label: 'Per night' },
                                    ]}
                                />
                            )}
                        </div>

                        {hasPackages && (
                            <div className="flex space-x-4 mt-4">
                                <Toggle
                                    enabled={!!typeData?.settings?.packageOnly}
                                    setEnabled={newValue =>
                                        setTypeData(old => ({
                                            ...old,
                                            settings: {
                                                ...old?.settings,
                                                packageOnly: newValue,
                                            },
                                        }))
                                    }
                                />
                                <div>
                                    <div className="">
                                        Require clients to book this as part of
                                        a package or subscription
                                    </div>
                                    <div className="text-gray-500">
                                        Clients must have a package or
                                        subscription that can be redeemed for
                                        this event type in order to book it.
                                    </div>
                                </div>
                            </div>
                        )}

                        {typeData?.price > 0 &&
                            !typeData?.settings?.packageOnly && (
                                <GateRequiresPaymentProcessor
                                    extraMessage="After connecting a payment processor you can configure payment options to require full payment, a deposit, or require a card on file."
                                    className="mt-4"
                                >
                                    <>
                                        <div className="input-label">
                                            Client payment options
                                        </div>
                                        <label className="py-4 flex items-center gap-x-4">
                                            <input
                                                type="radio"
                                                name="payment"
                                                checked={isPayInFull}
                                                onChange={e =>
                                                    e.target.checked &&
                                                    setPayment('full')
                                                }
                                            />
                                            Pay in full
                                        </label>
                                        {isPayInFull && (
                                            <label className="flex ml-8 items-center space-x-4 pb-4">
                                                <input
                                                    type="checkbox"
                                                    value={true}
                                                    checked={
                                                        !!typeData?.settings
                                                            ?.allowSkipPayment
                                                    }
                                                    onChange={e =>
                                                        setTypeData({
                                                            ...typeData,
                                                            settings: {
                                                                ...typeData.settings,
                                                                allowSkipPayment:
                                                                    e.target
                                                                        .checked,
                                                            },
                                                        })
                                                    }
                                                />
                                                <div>
                                                    Also give option to skip
                                                    payment (ex., to accept
                                                    payment in person)
                                                </div>
                                            </label>
                                        )}

                                        <label className="py-4 border-t flex items-center gap-x-4 -mr-4 pr-4">
                                            <input
                                                type="radio"
                                                name="payment"
                                                checked={
                                                    typeData?.settings
                                                        ?.payment === 'none'
                                                }
                                                onChange={e =>
                                                    e.target.checked &&
                                                    setPayment('none')
                                                }
                                            />
                                            Skip paying
                                        </label>

                                        <label className="py-2 pt-4 border-t flex items-center -mr-4 pr-4 gap-x-4">
                                            <input
                                                type="radio"
                                                name="payment"
                                                checked={
                                                    typeData?.settings
                                                        ?.payment === 'deposit'
                                                }
                                                onChange={e =>
                                                    e.target.checked &&
                                                    setPayment('deposit')
                                                }
                                            />
                                            Pay a deposit
                                        </label>
                                        {typeData?.settings?.payment ===
                                            'deposit' && (
                                            <>
                                                <MoneyInput
                                                    value={
                                                        typeData?.settings
                                                            ?.payDepositAmount ||
                                                        ''
                                                    }
                                                    onChange={value =>
                                                        setTypeData({
                                                            ...typeData,
                                                            settings: {
                                                                ...typeData.settings,
                                                                payDepositAmount:
                                                                    Math.min(
                                                                        value,
                                                                        typeData.price,
                                                                    ),
                                                            },
                                                        })
                                                    }
                                                    className="w-32 mb-4"
                                                    autoFocus={true}
                                                />
                                                {typeData?.settings
                                                    ?.payDepositAmount >=
                                                    typeData.price && (
                                                    <div className="text-red-600 text-sm mt-1">
                                                        Deposit amount must be
                                                        less than the event type
                                                        price
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <label className="py-4 border-t flex items-center gap-x-4 -mr-4 pr-4">
                                            <input
                                                type="radio"
                                                name="payment"
                                                checked={
                                                    typeData?.settings
                                                        ?.payment === 'vault'
                                                }
                                                onChange={e =>
                                                    e.target.checked &&
                                                    setPayment('vault')
                                                }
                                            />
                                            Require a valid credit card, but
                                            don't charge
                                        </label>
                                        {typeData?.settings?.payment ===
                                            'vault' && (
                                            <label className="flex ml-8 items-center space-x-4">
                                                <input
                                                    type="checkbox"
                                                    value={true}
                                                    checked={
                                                        !!typeData?.settings
                                                            ?.allowFullPayment
                                                    }
                                                    onChange={e =>
                                                        setTypeData({
                                                            ...typeData,
                                                            settings: {
                                                                ...typeData.settings,
                                                                allowFullPayment:
                                                                    e.target
                                                                        .checked,
                                                            },
                                                        })
                                                    }
                                                />
                                                <div>
                                                    Also allow full payment
                                                </div>
                                            </label>
                                        )}
                                    </>
                                </GateRequiresPaymentProcessor>
                            )}
                    </div>

                    {isClass && (
                        <>
                            <div className="panel-label mt-6">Schedule</div>
                            <ClassInstances
                                isSeries={isSeries}
                                instances={typeData?.classes}
                                groupLabels={groupLabels}
                                setGroupLabels={setGroupLabels}
                                typeId={typeId}
                                type={typeData}
                                setInstances={newValue =>
                                    setTypeData(old => ({
                                        ...old,
                                        classes: newValue,
                                    }))
                                }
                                onClassUpdated={() => {
                                    refetchDetails();
                                }}
                            />
                        </>
                    )}

                    {!typeData?.is_class && (
                        <>
                            <div className="panel-label mt-6">
                                This can be booked with
                            </div>
                            <div className="panel pl-6 py-3">
                                {!typeData?.bookable_resources?.length &&
                                    typeId && (
                                        <ErrorMessage className="mb-2">
                                            Please choose what this type can be
                                            booked with, people are currently
                                            unable to book this event type.
                                        </ErrorMessage>
                                    )}
                                <BookableResourcesCheckboxes
                                    id="bookable_resources"
                                    value={typeData?.bookable_resources}
                                    onChange={newValue =>
                                        setTypeData({
                                            ...typeData,
                                            bookable_resources: newValue,
                                        })
                                    }
                                />

                                {(typeData?.bookable_resources?.length > 1 ||
                                    typeData?.settings
                                        ?.numberBookableResourcesRequired >
                                        1) && (
                                    <NumberBookableResourcesRequiredSetting
                                        className="mt-4 border-t pt-4"
                                        value={
                                            typeData?.settings
                                                ?.numberBookableResourcesRequired
                                        }
                                        onChange={newValue =>
                                            setTypeData({
                                                ...typeData,
                                                settings: {
                                                    ...typeData?.settings,
                                                    numberBookableResourcesRequired:
                                                        newValue,
                                                },
                                            })
                                        }
                                    />
                                )}
                            </div>
                        </>
                    )}
                    <div className="mb-10">
                        <div
                            className={`${isDirty ? 'fixed z-[6] pl-10 -ml-10 bottom-0 bg-gray-100 w-full py-2' : ''}`}
                        >
                            <button
                                type="submit"
                                className={`${isDirty ? '' : 'mt-4'} btn-save`}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};

export default EventTypeEditPage;
